import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/cn";

/**
 * Right-side slide-over panel. Uses dir="ltr" on the shell so labels stay readable
 * even when the app root has RTL or mixed direction.
 */
export function Drawer({
  open: openProp,
  visible,
  title,
  /** Subtitle under title (e.g. breadcrumb) — keep short */
  description,
  onClose,
  children,
  width = 520,
  footer,
  className,
  bodyClassName,
}) {
  const open = openProp ?? visible ?? false;

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const widthStyle =
    typeof width === "number"
      ? { width: `min(calc(100vw - 1rem), ${width}px)` }
      : { width };

  const node = (
    <div className="fixed inset-0 z-[100]" dir="ltr" lang="en">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/45 backdrop-blur-[2px] transition-opacity"
        aria-label="Close panel"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "drawer-title" : undefined}
        className={cn(
          "absolute right-0 top-0 flex max-h-[100dvh] flex-col bg-white shadow-2xl ring-1 ring-slate-900/8",
          className
        )}
        style={widthStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0 flex-1">
            {title != null ? (
              <h2
                id="drawer-title"
                className="text-lg font-semibold leading-snug text-slate-900"
              >
                {title}
              </h2>
            ) : null}
            {description != null && description !== false ? (
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="shrink-0 rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-800"
            onClick={onClose}
            aria-label="Close"
          >
            <span className="text-xl leading-none">×</span>
          </button>
        </div>
        <div
          className={cn(
            "min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 text-left",
            bodyClassName
          )}
        >
          {children}
        </div>
        {footer != null ? (
          <div className="shrink-0 border-t border-slate-100 px-5 py-3">{footer}</div>
        ) : null}
      </aside>
    </div>
  );

  return createPortal(node, document.body);
}
